import { useState } from 'react';
import { useSchemaDesignerStore, TableData } from '@/stores/schema-designer-store';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RiTableLine, RiAddLine } from '@remixicon/react';
import { toast } from 'sonner';

export const AddTableDialog = () => {
  const [open, setOpen] = useState(false);
  const [tableName, setTableName] = useState('');
  const [tableComment, setTableComment] = useState('');
  const { addTable, nodes } = useSchemaDesignerStore();

  const handleAddTable = () => {
    const trimmedName = tableName.trim();

    if (!trimmedName) {
      toast.error('Table name is required');
      return;
    }

    // Check if table name already exists
    if (nodes.some((node) => node.data.name.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error(`Table "${trimmedName}" already exists`);
      return;
    }

    // Validate table name format
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedName)) {
      toast.error('Table name must start with a letter or underscore and contain only letters, numbers, and underscores');
      return;
    }

    const newTable: TableData = {
      name: trimmedName,
      columns: [],
      indexes: [],
      comment: tableComment.trim() || undefined,
    };

    addTable(newTable);
    toast.success(`Table "${trimmedName}" created`);

    // Reset and close
    setTableName('');
    setTableComment('');
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && tableName.trim()) {
      e.preventDefault();
      handleAddTable();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" size="sm">
          <RiTableLine className="h-4 w-4" />
          Add Table
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RiTableLine className="h-5 w-5 text-primary" />
            Add New Table
          </DialogTitle>
          <DialogDescription>
            Create a new table in your schema. You can add columns after creating the table.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="tableName" className="text-sm font-medium">
              Table Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="tableName"
              placeholder="e.g., users, products, orders"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Use lowercase with underscores (e.g., user_profiles)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tableComment" className="text-sm font-medium">
              Comment <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="tableComment"
              placeholder="Brief description of this table..."
              value={tableComment}
              onChange={(e) => setTableComment(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddTable} disabled={!tableName.trim()} className="gap-2">
            <RiAddLine className="h-4 w-4" />
            Create Table
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
